import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FileText } from "lucide-react";
import api from "@/api";

export default function Setup() {
  const [form, setForm] = useState({ email: "", password: "", full_name: "" });
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/check-setup").then((r) => {
      if (r.data.setup_complete) navigate("/login");
      setChecking(false);
    }).catch(() => setChecking(false));
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { setError("Password must be at least 8 characters"); return; }
    setLoading(true);
    setError("");
    try {
      const r = await api.post("/auth/setup", form);
      localStorage.setItem("token", r.data.token);
      localStorage.setItem("user", JSON.stringify(r.data.user));
      navigate("/admin");
    } catch (err) {
      setError(err.response?.data?.detail || "Setup failed");
    } finally {
      setLoading(false);
    }
  };

  if (checking) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
      <div className="w-8 h-8 border-2 border-[#006080] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] p-4">
      <div className="w-full max-w-md">
        <div className="bg-white border border-slate-200 rounded-lg p-8">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-8 h-8 bg-[#006080] rounded-md flex items-center justify-center">
              <FileText size={16} className="text-white" />
            </div>
            <span className="font-bold text-slate-900 text-lg">DocIntel</span>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-1">Initial Setup</h2>
          <p className="text-sm text-slate-500 mb-6">Create your Super Admin account to get started.</p>

          {error && (
            <div data-testid="setup-error" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {[
              { label: "Full Name", key: "full_name", type: "text", placeholder: "John Doe" },
              { label: "Email Address", key: "email", type: "email", placeholder: "admin@company.com" },
              { label: "Password", key: "password", type: "password", placeholder: "Min 8 characters" },
            ].map(({ label, key, type, placeholder }) => (
              <div key={key}>
                <label className="text-xs font-bold tracking-wide uppercase text-slate-500 block mb-1.5">{label}</label>
                <input
                  data-testid={`setup-${key}-input`}
                  type={type}
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  required
                  placeholder={placeholder}
                  className="w-full h-10 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] focus:border-[#006080] bg-white"
                />
              </div>
            ))}
            <button
              data-testid="setup-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-10 bg-[#006080] hover:bg-[#004B66] text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50"
            >
              {loading ? "Creating account..." : "Create Super Admin Account"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
